import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/catalyst/Button";
import {
	Combobox,
	ComboboxLabel,
	ComboboxOption,
} from "@/components/catalyst/Combobox";
import {
	Dialog,
	DialogActions,
	DialogBody,
	DialogDescription,
	DialogTitle,
} from "@/components/catalyst/Dialog";
import { Field, Label } from "@/components/catalyst/Fieldset";
import { Input } from "@/components/catalyst/Input";
import { orpc } from "@/orpc/client";
import { COUNTRIES } from "@/util/countries";

interface BillingAddress {
	line1: string | null;
	line2: string | null;
	city: string | null;
	state: string | null;
	postalCode: string | null;
	country: string | null;
}

export function UpdateBillingAddressDialog({
	isOpen,
	onClose,
	currentAddress,
	currentName,
}: {
	isOpen: boolean;
	onClose: () => void;
	currentAddress: BillingAddress | null;
	currentName: string | null;
}) {
	const queryClient = useQueryClient();

	const [formData, setFormData] = useState({
		name: currentName ?? "",
		line1: currentAddress?.line1 ?? "",
		line2: currentAddress?.line2 ?? "",
		city: currentAddress?.city ?? "",
		state: currentAddress?.state ?? "",
		postalCode: currentAddress?.postalCode ?? "",
		country: currentAddress?.country ?? "US",
	});

	const selectedCountry =
		COUNTRIES.find((c) => c.code === formData.country) ??
		COUNTRIES.find((c) => c.code === "US");

	const { mutate: updateAddress, isPending } = useMutation(
		orpc.billing.updateBillingAddress.mutationOptions({
			onSuccess: () => {
				toast.success("Billing address updated successfully!");
				queryClient.invalidateQueries({
					queryKey: orpc.billing.getBillingDetails.queryOptions().queryKey,
				});
				onClose();
			},
			onError: (error) => {
				toast.error(error.message || "Failed to update billing address");
			},
		}),
	);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		updateAddress({
			name: formData.name,
			line1: formData.line1,
			line2: formData.line2 || undefined,
			city: formData.city,
			state: formData.state || undefined,
			postalCode: formData.postalCode,
			country: formData.country,
		});
	};

	const handleChange = (field: keyof typeof formData, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
	};

	return (
		<Dialog open={isOpen} onClose={onClose}>
			<DialogTitle>Update Billing Address</DialogTitle>
			<DialogDescription>
				Update your billing address for invoices and receipts.
			</DialogDescription>
			<form onSubmit={handleSubmit}>
				<DialogBody className="space-y-4">
					<Field>
						<Label>Full Name</Label>
						<Input
							value={formData.name}
							onChange={(e) => handleChange("name", e.target.value)}
							required
						/>
					</Field>

					<Field>
						<Label>Address Line 1</Label>
						<Input
							value={formData.line1}
							onChange={(e) => handleChange("line1", e.target.value)}
							required
						/>
					</Field>

					<Field>
						<Label>Address Line 2 (Optional)</Label>
						<Input
							value={formData.line2}
							onChange={(e) => handleChange("line2", e.target.value)}
						/>
					</Field>

					<div className="grid grid-cols-2 gap-4">
						<Field>
							<Label>City</Label>
							<Input
								value={formData.city}
								onChange={(e) => handleChange("city", e.target.value)}
								required
							/>
						</Field>

						<Field>
							<Label>State / Province</Label>
							<Input
								value={formData.state}
								onChange={(e) => handleChange("state", e.target.value)}
							/>
						</Field>
					</div>

					<div className="grid grid-cols-2 gap-4">
						<Field>
							<Label>Postal Code</Label>
							<Input
								value={formData.postalCode}
								onChange={(e) => handleChange("postalCode", e.target.value)}
								required
							/>
						</Field>

						<Field>
							<Label>Country</Label>
							<Combobox
								options={COUNTRIES}
								value={selectedCountry}
								onChange={(country) => {
									if (country) {
										handleChange("country", country.code);
									}
								}}
								displayValue={(country) => country?.name}
								placeholder="Select country"
							>
								{(country) => (
									<ComboboxOption key={country.code} value={country}>
										<ComboboxLabel>{country.name}</ComboboxLabel>
									</ComboboxOption>
								)}
							</Combobox>
						</Field>
					</div>
				</DialogBody>
				<DialogActions>
					<Button outline onClick={onClose} type="button">
						Cancel
					</Button>
					<Button type="submit" color="dark/white" disabled={isPending}>
						{isPending ? "Saving..." : "Save Address"}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
}
